/*
 * Variant picker v2 - in and out of stock sizing UI (Teamwork task 40958118)
 *
 * The theme (theme.js, SelloutVariants) already marks every option input with
 * .sold-out or .unavailable for the hypothetical selection "this value plus the
 * other currently selected options", and re-runs that after every option
 * change. That gives bidirectional availability for free: picking a size
 * updates the colour swatches, picking a colour updates the sizes.
 *
 * This script only adds what the CSS cannot:
 *  - a title / accessible description on out-of-stock options, refreshed after
 *    each variant change;
 *  - removal of half sizes that are not available for the current selection
 *    (request 1). Hidden options stay in the DOM and keep their checked state,
 *    so the theme resolves exactly the same variant as before and the gallery
 *    still switches to the correct colourway (request 2). The option that is
 *    currently selected is never hidden, so the form keeps showing which size
 *    is chosen and the notify hand-off stays reachable;
 *  - the "Get notified" hand-off: when a shopper clicks an out-of-stock option
 *    the theme selects that variant, hides Add to Cart and Klaviyo Back in Stock
 *    swaps in its notify button (#klaviyo-bis-button-container). We wait for
 *    that button, bring it into view and focus it so the tap lands on the
 *    restock alert for that exact size. No new plumbing is added.
 */
(function () {
  'use strict';

  var PICKER = '[data-variant-picker-v2]';
  var OUT = ['sold-out', 'unavailable'];
  var BIS_CONTAINER = '#klaviyo-bis-button-container';
  var HIDDEN = 'vp2-half-out';
  /* Half sizes are written M4.5/W5.5, so the decimal point is the marker.
     Whole sizes (M04/W05, M14/W15) never match. */
  var HALF_SIZE = /\d\.5/;
  var pendingNotifyFocus = null;
  var repaintQueued = false;

  function isOut(input) {
    return OUT.some(function (c) { return input.classList.contains(c); });
  }

  function labelFor(picker, input) {
    if (input.id) {
      var byFor = picker.querySelector('label[for="' + input.id.replace(/"/g, '\\"') + '"]');
      if (byFor) return byFor;
    }
    return input.nextElementSibling && input.nextElementSibling.tagName === 'LABEL' ? input.nextElementSibling : null;
  }

  /* Only a size cell (.radio__button) may ever be removed. Colour swatches use
     .variant__swatch__button or <radio-swatch>, so they never match and are
     always left on the page. */
  function cellFor(input) {
    return input.closest('.radio__button');
  }

  function decorate(picker) {
    var hint = picker.getAttribute('data-notify-hint') || '';
    var inputs = picker.querySelectorAll('input[type="radio"][name^="options"]');
    var remove = [];
    Array.prototype.forEach.call(inputs, function (input) {
      var label = labelFor(picker, input);
      if (!label) return;
      var out = isOut(input);
      label.classList.toggle('is-out-of-stock', out);
      if (out) {
        if (hint) {
          label.setAttribute('title', hint);
          input.setAttribute('aria-description', hint);
        }
      } else {
        label.removeAttribute('title');
        input.removeAttribute('aria-description');
      }
      // Bell holder for colour-circle swatches, whose label:after is the theme's selected ring.
      if (label.closest('.swatch__button') && !label.querySelector('.vp2-bell')) {
        var bell = document.createElement('span');
        bell.className = 'vp2-bell';
        bell.setAttribute('aria-hidden', 'true');
        label.appendChild(bell);
      }
      var cell = cellFor(input);
      if (cell) {
        // Reset first so every pass starts from a clean visibility state.
        cell.classList.remove(HIDDEN);
        if (out && !input.checked && HALF_SIZE.test((label.textContent || '').trim())) {
          remove.push(cell);
        }
      }
    });
    applyRemoval(remove);
    watch(picker);
  }

  /* Remove the collected cells, unless that would leave a size group empty -
     an empty group is worse than a greyed-out one, so fall back to showing all. */
  function applyRemoval(remove) {
    var groups = [];
    remove.forEach(function (cell) {
      var fieldset = cell.closest('.radio__fieldset');
      if (!fieldset) return;
      var group = null;
      for (var i = 0; i < groups.length; i++) {
        if (groups[i].fieldset === fieldset) { group = groups[i]; break; }
      }
      if (!group) { group = { fieldset: fieldset, cells: [] }; groups.push(group); }
      group.cells.push(cell);
    });
    groups.forEach(function (group) {
      var all = group.fieldset.querySelectorAll('.radio__button');
      var left = 0;
      Array.prototype.forEach.call(all, function (cell) {
        if (!cell.classList.contains(HIDDEN)) left++;
      });
      // Cells queued for removal are not hidden yet, so `left` is what survives.
      if (left > 0) {
        group.cells.forEach(function (cell) { cell.classList.add(HIDDEN); });
      }
    });
  }

  function decorateAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    if (scope.matches && scope.matches(PICKER)) decorate(scope);
    Array.prototype.forEach.call(scope.querySelectorAll(PICKER), decorate);
  }

  /* SelloutVariants writes classes straight onto the inputs, which can happen
     outside a theme:variant:change (initial load, section reload). Watch for
     those so the picker never renders a stale availability state. The observer
     only reacts to class changes on inputs - this script's own changes land on
     labels and wrapper spans, so it does not re-trigger itself. */
  var watched = typeof WeakSet !== 'undefined' ? new WeakSet() : null;

  function watch(picker) {
    if (!watched || watched.has(picker) || typeof MutationObserver === 'undefined') return;
    watched.add(picker);
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].target && mutations[i].target.tagName === 'INPUT') {
          queueRepaint(picker);
          return;
        }
      }
    }).observe(picker, { subtree: true, attributes: true, attributeFilter: ['class'] });
  }

  function queueRepaint(root) {
    if (repaintQueued) return;
    repaintQueued = true;
    window.setTimeout(function () {
      repaintQueued = false;
      decorateAll(root || document);
    }, 0);
  }

  function inViewport(el) {
    var r = el.getBoundingClientRect();
    var h = window.innerHeight || document.documentElement.clientHeight;
    return r.top >= 0 && r.bottom <= h;
  }

  function focusNotifyButton(section) {
    var scope = section || document;
    var started = Date.now();
    (function poll() {
      var container = scope.querySelector(BIS_CONTAINER) || document.querySelector(BIS_CONTAINER);
      var button = container ? container.querySelector('button') : null;
      if (button && container.offsetParent !== null) {
        if (!inViewport(button)) {
          button.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        try { button.focus({ preventScroll: true }); } catch (e) { button.focus(); }
        return;
      }
      if (Date.now() - started < 2500) {
        window.setTimeout(poll, 100);
      }
    })();
  }

  // Remember when a shopper deliberately taps an out-of-stock option so the
  // notify hand-off only runs on that gesture, never on page load.
  document.addEventListener('pointerdown', function (event) {
    var label = event.target.closest ? event.target.closest(PICKER + ' label') : null;
    if (!label) { pendingNotifyFocus = null; return; }
    var picker = label.closest(PICKER);
    var input = label.htmlFor ? document.getElementById(label.htmlFor) : label.previousElementSibling;
    pendingNotifyFocus = (picker && input && isOut(input)) ? Date.now() : null;
  }, true);

  // A size click can change which options are removable even when the theme's
  // availability classes come out identical, so repaint on selection too.
  document.addEventListener('change', function (event) {
    var picker = event.target && event.target.closest ? event.target.closest(PICKER) : null;
    if (picker && event.target.type === 'radio') queueRepaint(picker);
  }, true);

  document.addEventListener('theme:variant:change', function (event) {
    var section = event.target && event.target.closest ? event.target.closest('[data-section-id]') : null;
    // SelloutVariants runs after this event inside the theme's pushState, so refresh on the next tick.
    window.setTimeout(function () { decorateAll(section || document); }, 0);
    var variant = event.detail && event.detail.variant;
    if (variant && variant.available === false && pendingNotifyFocus && Date.now() - pendingNotifyFocus < 3000) {
      pendingNotifyFocus = null;
      focusNotifyButton(section);
    }
  });

  document.addEventListener('shopify:section:load', function (event) {
    window.setTimeout(function () { decorateAll(event.target); }, 0);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { window.setTimeout(decorateAll, 0); });
  } else {
    window.setTimeout(decorateAll, 0);
  }
})();
